import {Platform} from 'react-native';
import DeviceInfo from 'react-native-device-info';
import {displayIncomingCallFromPush, endIncomingCallFromPush} from './callKeepClient';
import {registerVoipDeviceToken} from './voipApi';
import {buildDeviceMetadata} from './pushNotifications';
import {getAccessToken} from '../helpers/storage';

/**
 * Apple PushKit VoIP push (iOS only).
 *
 * FCM cannot deliver to PushKit tokens, so we register a dedicated VoIP token
 * and, on every incoming VoIP push, IMMEDIATELY report a call to CallKit
 * (iOS 13+ terminates apps that don't). This is what makes an incoming call
 * ring when the app is backgrounded or fully killed.
 *
 * The native module (react-native-voip-push-notification) is loaded lazily +
 * guarded, so builds without it (or Android) are safe no-ops.
 */

let VoipPushNotification = null;
let started = false;
let lastVoipToken = ''; // last token successfully uploaded
let pendingVoipToken = ''; // last token received (uploaded or not)

function getVoipModule() {
    if (VoipPushNotification !== null) {
        return VoipPushNotification;
    }
    try {
        // eslint-disable-next-line global-require
        VoipPushNotification = require('react-native-voip-push-notification').default;
    } catch (_error) {
        VoipPushNotification = false;
    }
    return VoipPushNotification;
}

/**
 * Upload the PushKit token to the backend as an `ios-voip` device. Uses a
 * distinct deviceId suffix so it never overwrites the standard FCM device row.
 *
 * @param {string} token PushKit VoIP token.
 */
async function uploadVoipToken(token) {
    if (!token) {
        return;
    }
    // Always remember the latest token so a pre-login arrival is not lost:
    // refreshVoipTokenRegistration() re-sends it once the user signs in.
    pendingVoipToken = token;
    if (token === lastVoipToken) {
        return;
    }
    const accessToken = await getAccessToken().catch(() => null);
    if (!accessToken) {
        return;
    }
    try {
        const meta = await buildDeviceMetadata();
        await registerVoipDeviceToken({
            token,
            platform: 'ios-voip',
            deviceId: meta.deviceId ? `${meta.deviceId}:voip` : undefined,
            deviceName: meta.deviceName,
            appVersion: meta.appVersion,
            osVersion: meta.osVersion,
            // Picks the APNs VoIP topic `<bundleId>.voip` — each app variant
            // has its own bundle id, a single server topic only reached one.
            bundleId: DeviceInfo.getBundleId() || undefined,
        });
        lastVoipToken = token;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[voip] VoIP token registration failed:', error?.message || error);
    }
}

/**
 * Start PushKit: register the token and wire the incoming-push → CallKit ring.
 * Idempotent. Must be called at module scope (index.js) so listeners exist on a
 * cold, push-triggered launch.
 *
 * @returns {void}
 */
export function setupVoipPushKit() {
    if (Platform.OS !== 'ios' || started) {
        return;
    }
    const voip = getVoipModule();
    if (!voip) {
        return;
    }
    started = true;

    try {
        // Cold-launch: events buffered before JS was ready are replayed here.
        voip.addEventListener('didLoadWithEvents', (events) => {
            if (!Array.isArray(events)) {
                return;
            }
            events.forEach((event) => {
                if (event?.name === 'RNVoipPushRemoteNotificationsRegisteredEvent') {
                    uploadVoipToken(event.data);
                } else if (event?.name === 'RNVoipPushRemoteNotificationReceivedEvent') {
                    handleIncomingVoipPush(event.data, voip);
                }
            });
        });

        voip.addEventListener('register', (token) => uploadVoipToken(token));

        voip.addEventListener('notification', (notification) => handleIncomingVoipPush(notification, voip));

        voip.registerVoipToken();
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[voip] PushKit setup failed:', error?.message || error);
    }
}

/**
 * A VoIP push arrived. The native PushKit handler (AppDelegate, injected by
 * plugins/withVoipIosAppDelegate.js) has ALREADY reported it to CallKit with
 * the backend `uuid` — reporting again from JS would ring a second call. Here
 * we only bind uuid ↔ callId + start SIP registration (incoming_call), or
 * clean up the app-side call state (call_ended; native already ended it).
 *
 * @param {object} payload The VoIP push payload (type, uuid, callId, from, contactName, …).
 * @param {object} voip The PushKit module.
 */
function handleIncomingVoipPush(payload, voip) {
    const data = payload || {};
    if (data.type === 'call_ended') {
        endIncomingCallFromPush(data);
        return;
    }
    const uuid = displayIncomingCallFromPush(
        {
            uuid: data.uuid,
            callId: data.callId,
            from: data.fromNumber || data.from,
            contactName: data.contactName,
            suggestedName: data.suggestedName,
        },
        {alreadyReported: true},
    );
    try {
        if (uuid && voip?.onVoipNotificationCompleted) {
            voip.onVoipNotificationCompleted(uuid);
        }
    } catch (_error) {
        /* noop */
    }
}

/**
 * Whether PushKit owns iOS incoming-call ringing on this device (a VoIP token
 * was received). The FCM `incoming_call` data push must then NOT ring CallKit
 * a second time.
 *
 * @returns {boolean}
 */
export function isVoipPushKitActive() {
    return Platform.OS === 'ios' && started && Boolean(pendingVoipToken || lastVoipToken);
}

/**
 * Re-register the VoIP token after login (the `register` event fires once at
 * app start, which may be before authentication).
 *
 * @returns {Promise<void>}
 */
export async function refreshVoipTokenRegistration() {
    if (Platform.OS !== 'ios') {
        return;
    }
    const token = pendingVoipToken || lastVoipToken;
    if (token) {
        lastVoipToken = '';
        await uploadVoipToken(token);
    }
}
